import * as cheerio from "cheerio"
import parse, { NodeType } from "node-html-parser"

import { convertHtmlToText } from "../convert-html"
import { MESSAGE_TYPOGRAPHY } from "../message-typography"

interface CheerioElement {
	tagName: string
}

// Pre-compiled patterns for sign-off phrase detection (used in splitEmailChunks)
const signOffPhrases =
	"mit freundlichen grü(?:ß|ss)en|mit besten grü(?:ß|ss)en|mit herzlichen grü(?:ß|ss)en|beste grü(?:ß|ss)e|liebe grü(?:ß|ss)e|herzliche grü(?:ß|ss)e|herzlichen gru(?:ß|ss)|schöne grü(?:ß|ss)e|vielen dank und liebe grü(?:ß|ss)e|vielen dank und grü(?:ß|ss)e|viele grü(?:ß|ss)e|freundliche grü(?:ß|ss)e|mfg|regards|best wishes|best regards|kind regards|warm regards|with best regards|yours truly|yours faithfully|yours sincerely|sincerely yours|cordially|respectfully|cordialement|bien cordialement|cordiales salutations|bien à vous|cordialement vôtre|cordiali saluti|distinti saluti|con cordiali saluti|un cordiale saluto|saludos cordiales|atentamente|un cordial saludo|met vriendelijke groet|met vriendelijke groeten|com os melhores cumprimentos|atenciosamente|med vänliga hälsningar|med vänlig hälsning|med venlig hilsen|vennlig hilsen"
const signOffExactPattern = new RegExp(
	`^(${signOffPhrases})(\\s*\\/\\s*(${signOffPhrases}))?[,.]?$`,
	"i",
)
const signOffStartPattern = new RegExp(
	`^(${signOffPhrases})(\\s*\\/\\s*(${signOffPhrases}))?[,.]?\\s`,
	"i",
)
const contactInfoPattern =
	/(?:tel[.:]|telefon[.:]|mobil\s|fax[.:]|phone[.:]|\+\d{1,3}\s*[\d(]|@[\w.-]+\.\w|www\.|https?:\/\/|geschäftsführ|registergericht|ust[\s.-]*id|hrb\s|company\s*number|vat\s|©\s*\d{4}|all rights reserved|confidential|disclaimer)/i
const outlookReplyHeaderPattern =
	/(?:(?:Von|From|De|Da|Van|Från|Fra)\s*:[\s\S]*?(?:Gesendet|Sent|Envoyé|Enviado|Inviato|Verzonden|Skickat|Sendt)\s*:[\s\S]*?(?:An|To|À|A|Aan|Till|Til)\s*:[\s\S]*?(?:Betreff|Subject|Objet|Asunto|Oggetto|Onderwerp|Ämne|Emne)\s*:)/i

// Separator lines the major clients insert when a message is forwarded (Gmail, Apple Mail, Outlook)
// across the locales we translate into. Distinct from reply quotes, so it identifies a true forward.
const forwardedMarkerPattern =
	/(?:-+\s*)?(?:forwarded message|weitergeleitete nachricht|message transféré|mensaje reenviado|messaggio inoltrato|mensagem encaminhada|doorgestuurd bericht|vidarebefordrat meddelande|videresendt melding)|(?:begin(?:n)?|anfang|début|inicio|inizio|início|start) (?:der weitergeleiteten nachricht|forwarded message|du message transféré|del mensaje reenviado|messaggio inoltrato|da mensagem encaminhada|van doorgestuurd bericht)/i

// True when the email body carries a client's "forwarded message" separator.
export const isForwardedEmail = (html: string | null | undefined): boolean => {
	if (!html) return false
	return isForwardedText(convertHtmlToText(html))
}

// Callers that already hold the plain text skip a second html-to-text parse.
export const isForwardedText = (text: string): boolean =>
	forwardedMarkerPattern.test(text)

export type HeadlineTag = "h1" | "h2" | "h3" | "h4"
const headlineTags: HeadlineTag[] = ["h1", "h2", "h3", "h4"]
export interface Headline {
	level: number
	text: string
}

export const removeStyleAttributes = (html: string): string => {
	const $ = cheerio.load(html)
	$("*").each((_, el) => {
		const element = $(el)
		element.removeAttr("style")
		element.removeAttr("color")
		element.removeAttr("bgcolor")
	})
	return $("body").html() ?? ""
}

// Runs after DOMPurify at the message render sites, whose default allowlist drops
// `target`, so it returns a fragment rather than a full document.
export const addTargetBlankToLinks = (html: string) => {
	const $ = cheerio.load(html)
	$("a").each((_, link) => {
		$(link).attr("target", "_blank")
		$(link).attr("rel", "noopener noreferrer")
	})
	return $("body").html() ?? ""
}

// One cheerio parse for both passes: a multi-megabyte body makes each parse cost
// whole seconds, so callers that need both must not run them separately.
export const linkAndStripStyles = (html: string): string => {
	const $ = cheerio.load(html)
	$("a").each((_, link) => {
		$(link).attr("target", "_blank")
		$(link).attr("rel", "noopener noreferrer")
	})
	$("*").each((_, el) => {
		const element = $(el)
		element.removeAttr("style")
		element.removeAttr("color")
		element.removeAttr("bgcolor")
	})
	return $("body").html() ?? ""
}

// Drops the composer's email reply scaffold (quoted history and signature), for
// channels where a reply carries only what the teammate wrote.
export const stripEmailHistory = (html: string): string => {
	if (!html.includes("data-email-history")) return html
	const $ = cheerio.load(html)
	$("[data-email-history]").remove()
	return $("body").html() ?? ""
}

export const wrapTablesForScroll = (html: string) => {
	const $ = cheerio.load(html)
	$("table").each((_, table) => {
		const $table = $(table)
		if ($table.parent(".tableScroll").length > 0) return
		$table.wrap('<div class="tableScroll"></div>')
	})
	return $("body").html() ?? ""
}

export const findAllHeadlines = (
	content: string | null,
	headlines: HeadlineTag[],
): Headline[] => {
	if (!content) return []
	const $ = cheerio.load(content)

	const items = $(headlines.join(",")).toArray()
	return items
		.map((hl) => {
			const tag = $(hl).get(0) as unknown as globalThis.Element
			const tagName = tag.tagName.toLowerCase() as HeadlineTag
			const level = headlineTags.indexOf(tagName) + 1
			const text = $(hl).text().trim()
			return {
				level,
				text,
			}
		})
		.filter((h) => h.text.length > 0)
}

export const convertNewlinesToBr = (text: string) =>
	text.replace(/\\n/g, "<br>").replace(/\n/g, "<br>")

export const normalizePlainTextNewlines = (content: string): string =>
	/<[a-z][\s\S]*>/i.test(content) ? content : content.replace(/\n/g, "<br>")

// Some origins store thread text as block HTML: wrapping that in another <p>
// nests paragraphs, and the parser splits the nesting into stray blank lines.
export const asBlockHtml = (text: string): string =>
	/<(?:p|div|blockquote|ul|ol|h[1-6]|pre|table)\b/i.test(text)
		? text
		: `<p>${normalizePlainTextNewlines(text)}</p>`

const BLOCK_TAGS = new Set([
	"p",
	"div",
	"blockquote",
	"ul",
	"ol",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"pre",
	"table",
	"hr",
	"figure",
	"details",
	"img",
	"video",
])

const wrapLooseRun = (run: string): string =>
	run
		.split(/\n\s*\n/)
		.map((chunk) => chunk.trim())
		.filter(Boolean)
		.map((chunk) => `<p>${chunk.replace(/\n/g, "<br>")}</p>`)
		.join("")

/**
 * The agent often writes loose text split by blank lines instead of <p> tags,
 * sometimes beside real blocks, and a browser runs all of it into one paragraph.
 */
export const paragraphsFromLooseHtml = (html: string): string => {
	if (!/\n\s*\n/.test(html)) return html
	const root = parse(html)
	let out = ""
	let run = ""
	for (const node of root.childNodes) {
		const tag =
			node.nodeType === NodeType.ELEMENT_NODE
				? (node as { rawTagName?: string }).rawTagName
				: null
		if (tag && BLOCK_TAGS.has(tag.toLowerCase())) {
			out += wrapLooseRun(run) + node.toString()
			run = ""
		} else {
			run += node.toString()
		}
	}
	return out + wrapLooseRun(run)
}

export const OUTBOUND_PARAGRAPH_STYLE = `margin:${MESSAGE_TYPOGRAPHY.paragraphMargin}`

/**
 * A mail client applies its own paragraph margin, which the composer does not,
 * so the sent mail would gain gaps the agent never saw. Inlining the composer's
 * spacing on every unstyled paragraph makes the two match. Paragraphs that carry
 * their own style keep their margin.
 */
export const inlineParagraphSpacing = (html: string): string =>
	html.replace(/<p(\s[^>]*)?>/gi, (tag: string, attributes?: string) => {
		if (attributes && /\sstyle\s*=/i.test(attributes)) return tag
		return `<p${attributes ?? ""} style="${OUTBOUND_PARAGRAPH_STYLE}">`
	})

export const EMAIL_QUOTE_STYLE =
	"margin:0px 0px 0px 0.8ex;border-left:1px solid rgb(204,204,204);padding-left:1ex;"

// Gmail's attribution line, verbatim: plain text, never bold, and the address
// only when we have one.
export const buildQuoteAttribution = ({
	day,
	time,
	name,
	email,
}: {
	day: string
	time: string
	name: string
	email?: string | null
}): string => {
	const sender = email ? `${name} &lt;${email}&gt;` : name
	return `<p>On ${day} at ${time}, ${sender} wrote:</p>`
}

const LOOM_ID_PATTERN = /loom\.com\/(?:share|embed)\/([a-zA-Z0-9-]+)/i
const LOOM_NODE_PATTERN = /<div[^>]*\sdata-loom-video[^>]*>[\s\S]*?<\/div>/gi

const loomIdFromUrl = (url: string): string | null =>
	LOOM_ID_PATTERN.exec(url)?.[1] ?? null

const loomPosterUrl = (id: string): string =>
	`https://cdn.loom.com/sessions/thumbnails/${id}-with-play.gif`

const loomShareUrl = (id: string): string => `https://www.loom.com/share/${id}`

// A node without a readable id is removed rather than sent as an embed no
// recipient can play.
const replaceLoomNodes = (html: string, render: (id: string) => string) =>
	html.replace(LOOM_NODE_PATTERN, (node: string) => {
		const src = /src\s*=\s*"([^"]+)"/i.exec(node)?.[1] ?? ""
		const id = loomIdFromUrl(src)
		return id ? render(id) : ""
	})

// Mail clients drop iframes, so a Loom node ships as its poster frame linking to the recording.
export const loomEmbedsToEmailLinks = (html: string): string =>
	replaceLoomNodes(html, (id) =>
		[
			`<a href="${loomShareUrl(id)}" target="_blank" rel="noopener noreferrer">`,
			`<img src="${loomPosterUrl(id)}" alt="Watch the Loom recording"`,
			` width="640" style="max-width:100%;height:auto;border-radius:8px;" />`,
			`</a>`,
		].join(""),
	)

// Chat surfaces drop iframes or render text only, so a Loom node ships as its share URL.
export const loomEmbedsToLinks = (html: string): string =>
	replaceLoomNodes(html, (id) => {
		const url = loomShareUrl(id)
		return `<p><a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a></p>`
	})

// Every shape a blank line arrives in: the composer's own `<p></p>`, a Tiptap
// round-trip's `<p><br></p>`, and the `<p>&nbsp;</p>` a mail client pastes in.
const BLANK_PARAGRAPH = /<p\b[^>]*>(?:\s|&nbsp;|&#160;|<br\s*\/?>)*<\/p>/gi

// A blank line ships as an empty paragraph, never as a bare `<br>` between two
// blocks. Outlook drops a `<br>` that is not inside a block element, which
// glued every paragraph of a reply into one dense block.
const emailBlankLine = () => `<p style="${OUTBOUND_PARAGRAPH_STYLE}"><br></p>`

/**
 * Turns what the composer holds into what goes on the wire. Every difference
 * between the two lives here: a blank line keeps its own empty paragraph,
 * quotes carry their bar as an inline style, and paragraph spacing is pinned to
 * the composer's so the sent mail reads the way it was drafted. A Loom embed
 * becomes its poster frame linking to the recording.
 */
export const prepareComposerHtmlForEmail = (html: string): string =>
	inlineParagraphSpacing(
		loomEmbedsToEmailLinks(html)
			.replace(BLANK_PARAGRAPH, emailBlankLine)
			.replace(
				/<blockquote(?![^>]*\sstyle=)([^>]*)>/gi,
				`<blockquote$1 style="${EMAIL_QUOTE_STYLE}">`,
			)
			.replace(/<blockquote[^>]*>\s*<\/blockquote>/gi, ""),
	)

export const removeCidImages = (html: string): string => {
	const $ = cheerio.load(html)
	$('img[src^="cid:"]').remove()
	return $.html()
}

export const removeStyleTags = (html: string): string => {
	const $ = cheerio.load(html)
	$("style").remove()
	return $.html()
}

/**
 * Converts block-level HTML elements to simple line breaks
 * to preserve text structure when stripping complex HTML
 */
export const normalizeBlockElements = (
	html: string,
	{ flattenTables = false }: { flattenTables?: boolean } = {},
): string => {
	const $ = cheerio.load(html)

	// Remove email thread separators (hr, quoted content indicators)
	$("hr").remove()
	$('div[id="appendonsend"]').remove()
	$('div[id="divRplyFwdMsg"]').remove()
	// Only remove common *email quote* blockquotes (keep user-authored blockquotes)
	$(
		'blockquote[type="cite"], blockquote.gmail_quote, blockquote[class$="gmail_quote"]',
	).remove()

	// Block elements that should have line breaks after them
	const blockElements = ["div", "p", "h1", "h2", "h3", "h4", "h5", "h6"]
	if (flattenTables) blockElements.push("tr")

	blockElements.forEach((tag) => {
		$(tag).each((_, el) => {
			const element = $(el)
			// Table-based signatures lose their whole layout once their cells are flattened
			if (!flattenTables && element.closest("table").length > 0) return
			const content = element.html()
			// Replace the block element with its content followed by a br
			element.replaceWith(`${content}<br>`)
		})
	})

	// Clean up multiple consecutive br tags
	let result = $.html()
	result = result.replace(/(<br\s*\/?>\s*){3,}/gi, "<br><br>")

	return result
}

export const trimTrailingEmptyElements = (html: string): string => {
	const $ = cheerio.load(html)
	const body = $("body")

	type CheerioSelection = typeof body

	const removeTrailingEmpty = (parent: CheerioSelection) => {
		while (parent.contents().length > 0) {
			const child = parent.contents().last()
			const tagName = (
				child.get(0) as unknown as CheerioElement
			)?.tagName?.toLowerCase()

			if (tagName === "br") {
				child.remove()
				continue
			}

			const text = child.text().trim()
			const isImage = tagName === "img"
			const hasImages = child.find("img").length > 0

			if (!text && !isImage && !hasImages) {
				child.remove()
			} else {
				break
			}
		}
	}

	const removeLeadingEmpty = (parent: CheerioSelection) => {
		while (parent.contents().length > 0) {
			const child = parent.contents().first()
			const tagName = (
				child.get(0) as unknown as CheerioElement
			)?.tagName?.toLowerCase()

			if (tagName === "br") {
				child.remove()
				continue
			}

			const text = child.text().trim()
			const isImage = tagName === "img"
			const hasImages = child.find("img").length > 0

			if (!text && !isImage && !hasImages) {
				child.remove()
			} else {
				break
			}
		}
	}

	removeLeadingEmpty(body)
	removeTrailingEmpty(body)
	return body.html() ?? ""
}

const cleanInvisibleContent = (html: string): string => {
	// Block elements collapse to a <br> so a sender's blank line keeps its height;
	// inline spans carry no line of their own and are dropped.
	const blockPatterns = [
		// eslint-disable-next-line no-misleading-character-class -- intentionally matching invisible/combining characters
		/<div[^>]*>[\s\u200B\u00AD\u034F\u180E\u2000-\u200F\u202A-\u202F\u205F-\u206F\uFEFF]+<\/div>/gi,
		// eslint-disable-next-line no-misleading-character-class -- intentionally matching invisible/combining characters
		/<p[^>]*>[\s\u200B\u00AD\u034F\u180E\u2000-\u200F\u202A-\u202F\u205F-\u206F\uFEFF]+<\/p>/gi,
	]
	const inlinePattern =
		// eslint-disable-next-line no-misleading-character-class -- intentionally matching invisible/combining characters
		/<span[^>]*>[\s\u200B\u00AD\u034F\u180E\u2000-\u200F\u202A-\u202F\u205F-\u206F\uFEFF]+<\/span>/gi

	let result = html
	for (const pattern of blockPatterns) {
		result = result.replace(pattern, "<br>")
	}
	return result.replace(inlinePattern, "")
}

export const splitEmailChunks = (
	emailHtmlContent: string,
): [visible: string, hidden: string] => {
	if (!emailHtmlContent) return ["", ""]

	let hiddenContent = ""

	const cleanedHtml = cleanInvisibleContent(emailHtmlContent)
	const parsed = parse(cleanedHtml).removeWhitespace()

	// Remove elements that contain only invisible/whitespace characters
	const invisibleCharsRegex = new RegExp(
		"[\\s\\u200B\\u00AD\\u034F\\u061C\\u180E" +
			"\\u2000-\\u200F\\u202A-\\u202F\\u205F-\\u206F\\uFEFF]",
		"g",
	)
	const allTextElements = parsed.querySelectorAll("div, span, p")
	for (const el of allTextElements) {
		if (!el.parentNode) continue
		const text = el.textContent || ""
		const cleanedText = text.replace(invisibleCharsRegex, "").trim()
		// An empty div with an id is a client marker (#appendonsend, #divRplyFwdMsg)
		// the quote detection below anchors on, so it has to survive this pass.
		if (
			!cleanedText &&
			!el.id &&
			el.childNodes.length <= 1 &&
			!el.querySelector("img, a, table")
		) {
			// An empty block element is the sender's blank line (Tiptap <p></p>,
			// Gmail <div><br></div>, Outlook <p><br></p>); keep it as a <br>.
			if (el.tagName === "SPAN") el.remove()
			else el.replaceWith("<br>")
		}
	}

	const chunks: [string, string] = [parsed.toString(), ""]

	const quotesToHide = [
		".gmail_quote",
		"[class$='gmail_quote']",
		".gmail_signature_prefix",
		"[class$='gmail_signature_prefix']",
		"[class$='gmail_signature']",
		".gmail_signature",
		"#Signature",
		".ms-outlook-mobile-signature",
		"#mail-editor-reference-message-container",
		".x-apple-transform-wrapper",
		"[class^='main-style-'] > .front-signature",
		"[name='messageSignatureSection']",
		'blockquote[type="cite"]',
		"[name='messageReplySection']",
		".shortwave-signature",
		"#shortwave-signature",
		".protonmail_signature_block",
		".protonmail_signature_block-empty",
		".protonmail_quote",
		".productlane-quote",
		"front-signature",
		'div[data-email-history="true"]',
		// Outlook prefixes classes and ids with "x_" every time it quotes a message,
		// so a nested copy of any marker above arrives as x_Signature, x_productlane-quote, ...
		"[class*='productlane-quote']",
		"[id$='Signature']",
		"[class*='gmail_signature']",
	]

	const blockquotes = parsed.querySelectorAll("blockquote[class]")
	if (blockquotes.length > 0) {
		hiddenContent += blockquotes[0]?.toString() ?? ""
		blockquotes.forEach((bq) => bq.remove())
	}

	const quoteHeaderPattern =
		/(?:On .+?wrote|Am .+?schrieb|Le .+?(?:a écrit|écrit)|El .+?escribió|Il .+?(?:ha scritto|scritto)|Em .+?escreveu|Op .+?schreef):?/i

	const plainBlockquotes = parsed.querySelectorAll("blockquote:not([class])")
	for (const bq of plainBlockquotes) {
		const text = bq.textContent?.trim() || ""
		if (quoteHeaderPattern.test(text)) {
			hiddenContent += bq.toString() ?? ""
			bq.remove()
		}
	}

	// Senders who answer inside the quoted history keep writing after the first
	// blockquote, so hiding everything below the header would drop their own words.
	const hasInlineReplyAfterQuote = (
		start: ReturnType<typeof parsed.querySelectorAll>[number],
	) => {
		let sibling = start.nextElementSibling
		let sawQuote = false
		while (sibling) {
			if (sibling.tagName === "BLOCKQUOTE") sawQuote = true
			else if (sawQuote && (sibling.textContent?.trim().length || 0) >= 40)
				return true
			sibling = sibling.nextElementSibling
		}
		return false
	}

	const allElements = parsed.querySelectorAll("div, p, span")
	for (const el of Array.from(allElements)) {
		const text = el.textContent?.trim() || ""
		const startsWithQuoteHeader = /^(?:On |Am |Le |El |Il |Em |Op )/i.test(text)
		const quoteVerbPattern =
			/(?:wrote|schrieb|a écrit|écrit|escribió|ha scritto|scritto|escreveu|schreef)(?:\s+[^:]+)?:?\s*$/i
		// Plain-text clients wrap the header at ~72 chars, so "wrote:" can sit
		// in the next one or two line elements.
		let headerText = text
		let sibling = el.nextElementSibling
		for (
			let i = 0;
			i < 2 && sibling && !quoteVerbPattern.test(headerText);
			i++, sibling = sibling.nextElementSibling
		) {
			headerText += ` ${sibling.textContent?.trim() || ""}`
		}
		const hasQuoteVerb = quoteVerbPattern.test(headerText)
		const isShortEnough = headerText.length < 500

		if (startsWithQuoteHeader && hasQuoteVerb && isShortEnough) {
			if (hasInlineReplyAfterQuote(el)) continue
			let nextElement = el.nextElementSibling
			hiddenContent += el.toString()
			el.remove()
			while (nextElement) {
				hiddenContent += nextElement.toString()
				const sibling = nextElement.nextElementSibling
				nextElement.remove()
				nextElement = sibling
			}
			break
		}
	}

	// Outlook desktop reply/forward headers containing Von/From, Gesendet/Sent, An/To, Betreff/Subject.
	// Detected by content pattern, not style attributes (style may be stripped before splitting).
	const removeElAndFollowing = (
		start: ReturnType<typeof parsed.querySelectorAll>[number],
	) => {
		let nextElement = start.nextElementSibling
		hiddenContent += start.toString()
		start.remove()
		while (nextElement) {
			hiddenContent += nextElement.toString()
			const sibling = nextElement.nextElementSibling
			nextElement.remove()
			nextElement = sibling
		}
	}

	const allDivs = parsed.querySelectorAll("div, p")
	for (const el of Array.from(allDivs)) {
		const text = el.textContent?.trim() || ""
		if (text.length > 600 || text.length < 20) continue
		if (!outlookReplyHeaderPattern.test(text)) continue
		// Ensure this is the innermost match — skip if a child also matches
		const childMatch = el
			.querySelectorAll("div, p")
			.find((child) =>
				outlookReplyHeaderPattern.test(child.textContent?.trim() || ""),
			)
		if (childMatch) continue

		// Save parent chain before removal (remove() nulls parentNode)
		const ancestors: (typeof el)[] = []
		let p = el.parentNode
		while (p && p !== parsed && "nextElementSibling" in p) {
			ancestors.push(p)
			p = p.parentNode
		}

		removeElAndFollowing(el)

		for (const anc of ancestors) {
			const ancNext = anc.nextElementSibling
			if (ancNext) {
				removeElAndFollowing(ancNext)
			}
		}
		break
	}

	quotesToHide.forEach((selector: string) => {
		const selectedItems = parsed.querySelectorAll(selector)
		selectedItems.forEach((m) => {
			hiddenContent += m.toString() ?? ""
			m.remove()
		})
	})

	// Productlane live-chat history blockquotes. Older messages were generated
	// without a class marker, so we also match bare blockquotes whose text
	// starts with "Live Chat Conversation:". Each round-trip through email
	// nests another copy, so even one such blockquote represents quoted history.
	const liveChatBlockquotes = parsed.querySelectorAll("blockquote")
	for (const bq of liveChatBlockquotes) {
		const text = bq.textContent?.trim() || ""
		if (text.startsWith("Live Chat Conversation:")) {
			hiddenContent += bq.toString()
			bq.remove()
		}
	}

	// Outlook desktop reply/forward header block; everything after it is quoted history.
	const replyHeaderBlock = parsed.querySelector("[id$='divRplyFwdMsg']")
	if (replyHeaderBlock) {
		const replyAncestors: (typeof replyHeaderBlock)[] = []
		let replyParent = replyHeaderBlock.parentNode
		while (
			replyParent &&
			replyParent !== parsed &&
			"nextElementSibling" in replyParent
		) {
			replyAncestors.push(replyParent)
			replyParent = replyParent.parentNode
		}
		removeElAndFollowing(replyHeaderBlock)
		for (const anc of replyAncestors) {
			const ancNext = anc.nextElementSibling
			if (ancNext) removeElAndFollowing(ancNext)
		}
	}

	// Outlook
	const forwardMessage = parsed.querySelector("[id$='appendonsend']")
	if (forwardMessage) {
		const parent = forwardMessage.parentNode
		if (parent) {
			let nextElement = forwardMessage.nextElementSibling
			while (nextElement) {
				if (nextElement.tagName === "DIV" || nextElement.tagName === "HR") {
					hiddenContent += nextElement.toString()
					const currentElement = nextElement
					nextElement = nextElement.nextElementSibling
					currentElement.remove()
				} else {
					nextElement = nextElement.nextElementSibling
				}
			}
		}
		hiddenContent += forwardMessage.toString()
		forwardMessage.remove()
	}

	const wordSection = parsed.querySelector("[class^='WordSection']")
	if (wordSection) {
		const divsInWordSection = wordSection.querySelectorAll(":scope > div")
		for (const div of divsInWordSection) {
			const hasContent =
				div.querySelectorAll("p, span, b, strong, i, ul, ol, li").length > 0
			const textContent = div.textContent?.trim() || ""
			const isQuotedOrEmpty =
				!hasContent ||
				textContent.length === 0 ||
				div.id === "mail-editor-reference-message-container" ||
				div.id === "appendonsend"
			if (isQuotedOrEmpty) {
				hiddenContent += div.toString()
				div.remove()
			}
		}
	}

	const msOutlookMobileSignatureId = parsed.querySelector(
		"#ms-outlook-mobile-signature",
	)
	if (msOutlookMobileSignatureId) {
		let nextElement = msOutlookMobileSignatureId.nextElementSibling
		hiddenContent += msOutlookMobileSignatureId.toString()
		msOutlookMobileSignatureId.remove()
		while (nextElement) {
			hiddenContent += nextElement.toString()
			const sibling = nextElement.nextElementSibling
			nextElement.remove()
			nextElement = sibling
		}
	}

	// Signature delimiter (-- or long dashes)
	const allBlocks = parsed.querySelectorAll("p, div, span")
	for (const el of Array.from(allBlocks)) {
		const text = el.textContent?.trim() || ""
		const isSignatureDelimiter =
			/^[- ]{10,}$/.test(text) || /^--(\s|$)/.test(text)
		if (isSignatureDelimiter) {
			let nextElement = el.nextElementSibling
			hiddenContent += el.toString()
			el.remove()
			while (nextElement) {
				hiddenContent += nextElement.toString()
				const sibling = nextElement.nextElementSibling
				nextElement.remove()
				nextElement = sibling
			}
			break
		}
	}

	// Sign-off phrase detection (e.g. "Mit freundlichen Grüßen", "Best regards")
	// Handles Outlook desktop and other clients where signatures have no class/id markers.
	// Matches formal sign-off phrases at the start of an element's text OR after only whitespace.
	// Requires contact info (phone, email, etc.) in the same element or following elements.
	const signOffBlocks = parsed.querySelectorAll("p, div, span")
	for (const el of Array.from(signOffBlocks)) {
		const text = el.textContent?.trim() || ""
		if (text.length === 0) continue
		const isExactMatch = text.length <= 80 && signOffExactPattern.test(text)
		const isStartMatch = !isExactMatch && signOffStartPattern.test(text)
		if (!isExactMatch && !isStartMatch) continue

		// Gather all text after this element (siblings + parent siblings) for contact info check
		let followingText = isStartMatch ? text : ""
		let next = el.nextElementSibling
		while (next) {
			followingText += next.textContent || ""
			next = next.nextElementSibling
		}
		let ancestor = el.parentNode
		while (
			ancestor &&
			ancestor !== parsed &&
			"nextElementSibling" in ancestor
		) {
			let parentNext = ancestor.nextElementSibling
			while (parentNext) {
				followingText += parentNext.textContent || ""
				parentNext = parentNext.nextElementSibling
			}
			ancestor = ancestor.parentNode
		}
		if (!contactInfoPattern.test(followingText)) continue

		// Save parent chain before removal (remove() nulls parentNode)
		const signOffAncestors: (typeof el)[] = []
		let signOffParent = el.parentNode
		while (
			signOffParent &&
			signOffParent !== parsed &&
			"nextElementSibling" in signOffParent
		) {
			signOffAncestors.push(signOffParent)
			signOffParent = signOffParent.parentNode
		}

		removeElAndFollowing(el)

		for (const anc of signOffAncestors) {
			const ancNext = anc.nextElementSibling
			if (ancNext) {
				removeElAndFollowing(ancNext)
			}
		}
		break
	}

	// Table-based signatures
	const tables = parsed.querySelectorAll("table")
	const topLevelTables = Array.from(tables).filter(
		(table) => !table.parentNode?.closest?.("table"),
	)

	const isSignatureTable = (tableText: string): boolean => {
		const looksLikeContent =
			/new submission|form submission|submitted|inquiry|request from/i.test(
				tableText,
			) ||
			/:\s*https?:\/\//i.test(tableText) ||
			/funding:|employees:|usecases?:|industry:|company:/i.test(tableText) ||
			(tableText.length > 500 &&
				(tableText.match(/https?:\/\//g) || []).length >= 2)
		if (looksLikeContent) return false
		return (
			/confidential|disclaimer|strictly forbidden|third party|written consent|no liability|accepts no liability/i.test(
				tableText,
			) ||
			(/\b(chief|ceo|cto|coo|cfo|director|manager|officer|president|vp|vice president|founder|co[-‑–—]?founder)\b/i.test(
				tableText,
			) &&
				tableText.length < 500) ||
			/©\s*\d{4}|all rights reserved/i.test(tableText) ||
			/\bvat\s+[a-z]{2}\s*\d|company\s+number\s+\d|registration\s+number/i.test(
				tableText,
			) ||
			(/\+\d{1,3}\s*\d{2,4}\s*\d{3,4}\s*\d{3,4}/i.test(tableText) &&
				tableText.length < 400)
		)
	}

	const signatureTablesToRemove: typeof topLevelTables = []

	for (let i = topLevelTables.length - 1; i >= 0; i--) {
		const table = topLevelTables[i]
		if (!table) continue
		const tableText = table.textContent?.toLowerCase() || ""
		if (isSignatureTable(tableText)) {
			let contentAfter = ""
			let nextEl = table.nextElementSibling
			while (nextEl) {
				const siblingTable = nextEl.querySelector?.("table")
				if (!siblingTable || !signatureTablesToRemove.includes(siblingTable)) {
					contentAfter += nextEl.textContent || ""
				}
				nextEl = nextEl.nextElementSibling
			}
			const parent = table.parentNode
			if (parent && parent !== parsed && "nextElementSibling" in parent) {
				let parentSibling = parent.nextElementSibling
				while (parentSibling) {
					const siblingTable = parentSibling.querySelector?.("table")
					if (
						!siblingTable ||
						!signatureTablesToRemove.includes(siblingTable)
					) {
						contentAfter += parentSibling.textContent || ""
					}
					parentSibling = parentSibling.nextElementSibling
				}
			}
			if (contentAfter.trim().length < 100) {
				signatureTablesToRemove.push(table)
			} else {
				break
			}
		}
	}

	for (const table of topLevelTables) {
		if (!table || signatureTablesToRemove.includes(table)) continue
		const tableText = table.textContent?.toLowerCase() || ""
		if (isSignatureTable(tableText)) {
			let contentBefore = ""
			let prevEl = table.previousElementSibling
			while (prevEl) {
				contentBefore += prevEl.textContent || ""
				prevEl = prevEl.previousElementSibling
			}
			const parent = table.parentNode
			if (parent && parent !== parsed && "previousElementSibling" in parent) {
				let parentSibling = parent.previousElementSibling
				while (parentSibling) {
					contentBefore += parentSibling.textContent || ""
					parentSibling = parentSibling.previousElementSibling
				}
			}
			if (contentBefore.trim().length < 100) {
				signatureTablesToRemove.push(table)
			} else {
				break
			}
		}
	}

	signatureTablesToRemove.reverse().forEach((table) => {
		hiddenContent += table.toString()
		table.remove()
	})

	// Branded signatures with no delimiter, sign-off phrase, or known class:
	// a logo image trailed by an email and a postal address. Requires body text
	// before the logo so a signature-only message is never fully hidden.
	const streetAddressPattern =
		/\d{1,6}\s+[\w.'-]+(?:\s+[\w.'-]+){0,5}\s+(?:road|rd|street|avenue|ave|boulevard|blvd|lane|drive|way|court|plaza|square|floor|suite|terrace|parkway|highway)\b/i
	// A structured mailto link is the signal we trust; a plain email typed inside
	// a prose sentence is not enough to hide the text that follows an image.
	const hasMailtoLink = (el: {
		getAttribute: (name: string) => string | null | undefined
		querySelectorAll: (
			sel: string,
		) => Array<{ getAttribute: (name: string) => string | null | undefined }>
	}): boolean => {
		const selfHref = el.getAttribute("href") || ""
		if (selfHref.toLowerCase().startsWith("mailto:")) return true
		return el
			.querySelectorAll("a")
			.some((a) =>
				(a.getAttribute("href") || "").toLowerCase().startsWith("mailto:"),
			)
	}
	for (const img of Array.from(parsed.querySelectorAll("img"))) {
		let followingText = ""
		let followingHasMailto = false
		let next = img.nextElementSibling
		while (next) {
			followingText += `${next.textContent || ""} `
			followingHasMailto = followingHasMailto || hasMailtoLink(next)
			next = next.nextElementSibling
		}
		let ancestor = img.parentNode
		while (
			ancestor &&
			ancestor !== parsed &&
			"nextElementSibling" in ancestor
		) {
			let parentNext = ancestor.nextElementSibling
			while (parentNext) {
				followingText += `${parentNext.textContent || ""} `
				followingHasMailto = followingHasMailto || hasMailtoLink(parentNext)
				parentNext = parentNext.nextElementSibling
			}
			ancestor = ancestor.parentNode
		}
		const looksLikeSignature =
			followingText.trim().length < 400 &&
			followingHasMailto &&
			streetAddressPattern.test(followingText)
		if (!looksLikeSignature) continue

		let precedingText = ""
		let prev = img.previousElementSibling
		while (prev) {
			precedingText += prev.textContent || ""
			prev = prev.previousElementSibling
		}
		let precedingAncestor = img.parentNode
		while (
			precedingAncestor &&
			precedingAncestor !== parsed &&
			"previousElementSibling" in precedingAncestor
		) {
			let parentPrev = precedingAncestor.previousElementSibling
			while (parentPrev) {
				precedingText += parentPrev.textContent || ""
				parentPrev = parentPrev.previousElementSibling
			}
			precedingAncestor = precedingAncestor.parentNode
		}
		if (precedingText.trim().length === 0) continue

		const imgAncestors: (typeof img)[] = []
		let imgParent = img.parentNode
		while (
			imgParent &&
			imgParent !== parsed &&
			"nextElementSibling" in imgParent
		) {
			imgAncestors.push(imgParent)
			imgParent = imgParent.parentNode
		}
		removeElAndFollowing(img)
		for (const anc of imgAncestors) {
			const ancNext = anc.nextElementSibling
			if (ancNext) removeElAndFollowing(ancNext)
		}
		break
	}

	// Remove hollow tracking images (Superhuman)
	const images = parsed.querySelectorAll("img")
	images.forEach((image) => {
		const width = image.getAttribute("width")
		const height = image.getAttribute("height")
		if (
			(width === "1" && height === "0") ||
			(height === "1" && width === "0")
		) {
			image.remove()
		}
	})

	const regExp = /<div><\/div>|<p><\/p>|<div><br><\/div>/g
	const brRegExp = /<br>(?=(<[^<>]+?>)*$)/g
	const regExpSuperhuman =
		/<br><\/div><\/div><div><div><img[^>]*><\/div><br><\/div><br><br><\/div><\/div>/g
	const regExpEmptyImage = /<img[^>]*\s+src\s*=\s*['"]{2}[^>]*>/g

	chunks[0] = parsed
		.toString()
		.replace(regExp, "<br>")
		.replace(regExpSuperhuman, "")
		.replace(regExpEmptyImage, "")
		.replace(brRegExp, "")

	chunks[1] = hiddenContent.replace(regExp, "")
	return chunks
}

/**
 * Visible part of a message body, with signatures, quoted replies and forwarded
 * history removed. Falls back to the input when stripping leaves nothing behind.
 */
export const stripQuotedContent = (html: string | null | undefined): string => {
	if (!html) return ""
	const visible = splitEmailChunks(html)[0] ?? ""
	const hasVisibleText = !!convertHtmlToText(visible, true).trim()
	const hasVisibleMedia = /<img|<a\s/i.test(visible)
	return hasVisibleText || hasVisibleMedia ? visible : html
}

const INBOX_PREVIEW_CHARS_LIMIT = 140

/** First line of an email body, for the inbox preview line next to the subject. */
export const extractInboxPreviewText = (
	html: string | null | undefined,
	maxLength = INBOX_PREVIEW_CHARS_LIMIT,
): string => {
	if (!html) return ""
	const text = convertHtmlToText(html, true).replace(/\s+/g, " ").trim()
	if (text.length <= maxLength) return text
	const truncated = text.slice(0, maxLength)
	const lastSpace = truncated.lastIndexOf(" ")
	const cut = lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated
	return `${cut.replace(/[\s,;:.!?-]+$/, "")}...`
}

const EMAIL_PREVIEW_CHARS_LIMIT = 1000

/** Short plain-text snippet of an email body, for list rendering. */
export const formatEmailHtmlContent = (emailContent: string): string => {
	const visible = splitEmailChunks(emailContent)[0]
	const text = convertHtmlToText(visible, false, { preserveLinks: true })
	return text.length > EMAIL_PREVIEW_CHARS_LIMIT
		? `${text.slice(0, EMAIL_PREVIEW_CHARS_LIMIT)}...`
		: text
}
